import React from 'react';
import toast from 'react-hot-toast';

export type ConnectionStatus = 'online' | 'offline';

type ConnectionStatusListener = (status: ConnectionStatus) => void;

// State
let currentStatus: ConnectionStatus =
  typeof navigator !== 'undefined' && navigator.onLine ? 'online' : 'offline';
let offlineToastId: string | undefined;
let offlineToastTimeout: ReturnType<typeof setTimeout> | undefined;
let isListening = false;
const listeners = new Set<ConnectionStatusListener>();

/**
 * Get the current connection status
 */
export const getCurrentConnectionStatus = (): ConnectionStatus => currentStatus;

/**
 * Subscribe to connection status changes
 */
export const subscribe = (listener: ConnectionStatusListener): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Notify all listeners of status change
 */
const notifyListeners = (status: ConnectionStatus): void => {
  listeners.forEach(listener => listener(status));
};

/**
 * Show offline toast message with dismiss button
 */
const showOfflineToast = (): void => {
  // Clear any pending timeout
  if (offlineToastTimeout) {
    clearTimeout(offlineToastTimeout);
    offlineToastTimeout = undefined;
  }

  // Don't show duplicate toasts
  if (offlineToastId) {
    return;
  }

  const Message = ({ t }: { t: { id: string } }) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <p style={{ flex: 1, margin: 0, padding: 0 }}>
        Connection is lost or unstable
      </p>
      <button
        style={{
          background: 'white',
          border: 'none',
          borderRadius: 4,
          padding: '5px 10px',
          marginLeft: 15,
        }}
        onClick={() => {
          toast.dismiss(t.id);
          offlineToastId = undefined;
          // Re-show toast after delay if still offline
          if (currentStatus === 'offline') {
            offlineToastTimeout = setTimeout(showOfflineToast, 4000);
          }
        }}>
        Dismiss
      </button>
    </div>
  );

  offlineToastId = toast.error((t: { id: string }) => <Message t={t} />, {
    duration: Infinity,
  });
};

/**
 * Dismiss offline toast
 */
const dismissOfflineToast = (): void => {
  if (offlineToastId) {
    toast.dismiss(offlineToastId);
    offlineToastId = undefined;
  }
  if (offlineToastTimeout) {
    clearTimeout(offlineToastTimeout);
    offlineToastTimeout = undefined;
  }
};

/**
 * Handle online event
 */
const handleOnline = (): void => {
  if (currentStatus === 'offline') {
    currentStatus = 'online';
    dismissOfflineToast();
    notifyListeners('online');
  }
};

/**
 * Handle offline event
 */
const handleOffline = (): void => {
  if (currentStatus === 'online') {
    currentStatus = 'offline';
    showOfflineToast();
    notifyListeners('offline');
  }
};

/**
 * Start listening to connection status changes.
 * Uses native browser online/offline events.
 */
export const startPolling = (): void => {
  if (isListening || typeof window === 'undefined') {
    return;
  }

  isListening = true;

  // Set initial status
  currentStatus = navigator.onLine ? 'online' : 'offline';

  // Show toast if starting in offline state
  if (currentStatus === 'offline') {
    showOfflineToast();
  }

  // Add event listeners
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
};

/**
 * Stop listening to connection status changes.
 */
export const stopPolling = (): void => {
  if (!isListening || typeof window === 'undefined') {
    return;
  }

  isListening = false;

  // Remove event listeners
  window.removeEventListener('online', handleOnline);
  window.removeEventListener('offline', handleOffline);

  // Clean up toasts
  dismissOfflineToast();
};

/**
 * @deprecated Use subscribe() instead. Kept for backward compatibility.
 * EventEmitter-like interface for existing code.
 */
export const connectionStatus = {
  on: (event: 'online' | 'offline', handler: () => void): void => {
    const listener: ConnectionStatusListener = status => {
      if (status === event) {
        handler();
      }
    };
    // Store reference for removal
    (
      handler as {
        __listener?: ConnectionStatusListener;
      }
    ).__listener = listener;
    listeners.add(listener);
  },
  off: (event: 'online' | 'offline', handler: () => void): void => {
    const listener = (handler as { __listener?: ConnectionStatusListener })
      .__listener;
    if (listener) {
      listeners.delete(listener);
    }
  },
  emit: (event: ConnectionStatus): void => {
    notifyListeners(event);
  },
};
